import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import { useContext, type ReactNode } from "react";

import { cx } from "@/lib/cx";
import { OverlayContentContext, useOverlayGrant } from "@/lib/overlay-gate";

/**
 * Radix `DropdownMenu`, styled to the system — flat, hairline-bordered, no
 * shadow, the same reasoning as `Popover.tsx`. Registers with the shared
 * overlay gate (`lib/overlay-gate.ts`) too — including `Popover.tsx`'s own
 * Story 15.3 `OverlayContentContext` exemption (see that file's identical
 * doc comment): a `DropdownMenu` rendered inside an already-open `Dialog`/
 * `Sheet` is that overlay's own visual child, not a second one competing
 * for the same slot. No current consumer nests one (this component's own
 * doc paragraph below), but leaving this primitive out while `Popover`
 * carries the fix would silently reintroduce the identical bug the moment
 * someone does.
 *
 * Built to satisfy Story 2.6's "build all four primitives on Radix" (the
 * spec's own judgement call 1) even though neither retrofit this story
 * ships needs a menu of several actions — remove-a-user and
 * delete-a-saved-view are each a single verb-labelled ghost button, per
 * DESIGN.md's own rule ("a destructive action in a table row... is a ghost
 * button labelled with its verb", not a generic menu trigger hiding it).
 * `Popover.tsx` carries this story's live proof of VC-2.6-05 instead (the
 * "Save this view" flow); this component has no live consumer yet, the
 * same honestly-stated position `Combobox.tsx` is in for judgement call 4.
 */
export interface DropdownMenuItemConfig {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  //: Owner-requested (2026-08-28) — a leading glyph next to `label`, e.g.
  //: `GlobalTopBar.tsx`'s own `LogOutIcon` on its "Sign out" item, so a
  //: menu entry conveys what it does at a glance, not only through its
  //: text. Optional: every pre-existing caller that never passes one still
  //: renders exactly as before.
  icon?: ReactNode;
}

export interface DropdownMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  items: DropdownMenuItemConfig[];
  align?: "start" | "center" | "end";
}

export function DropdownMenu({ open, onOpenChange, trigger, items, align = "end" }: DropdownMenuProps) {
  //: See `Popover.tsx`'s identical wiring — same reasoning, same
  //: unconditional-hook-call discipline (Rules of Hooks; the real gate
  //: request is always made and harmlessly lost while nested).
  const insideGrantedOverlay = useContext(OverlayContentContext);
  const gateGranted = useOverlayGrant(open);
  const granted = insideGrantedOverlay ? open : gateGranted;

  return (
    <RadixDropdownMenu.Root open={granted} onOpenChange={onOpenChange}>
      <RadixDropdownMenu.Trigger asChild>{trigger}</RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          align={align}
          sideOffset={8}
          className="z-50 origin-[var(--radix-popper-transform-origin)] rounded-md border border-border-hairline bg-surface p-2 data-[state=closed]:animate-menu-out data-[state=open]:animate-menu-in"
        >
          {items.map((item) => (
            <RadixDropdownMenu.Item
              key={item.label}
              disabled={item.disabled ?? false}
              onSelect={item.onSelect}
              className={cx(
                "flex h-tap-target-min cursor-pointer items-center gap-2 rounded-md px-3 text-body text-ink outline-none",
                "data-[highlighted]:bg-surface-hover",
              )}
            >
              {item.icon ? (
                <span className="flex shrink-0 items-center text-ink-secondary">{item.icon}</span>
              ) : null}
              {item.label}
            </RadixDropdownMenu.Item>
          ))}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}
